// src/components/AddTicketModalMobile.jsx
import React, { useContext, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slide,
  Box,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  FormHelperText,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LanguageContext } from "../context/LanguageContext";
import { PRIORITY_LEVELS, PRIORITY_COLORS, TICKET_TYPES } from "../constant";
import UploadFilesModal from "../uploaders/UploadFilesModal";

// Transition pour le Dialog
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function AddTicketModalMobile({
  open,
  onClose,
  accessibleProjects,
  selectedProject,
  handleProjectChange,
  handleSubmitTicket,
  loading,
}) {
  const { translate } = useContext(LanguageContext);
  const theme = useTheme();
  // plein écran pour les petits écrans
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // champs du formulaire
  const [ticketName, setTicketName] = useState("");
  const [originalQuestion, setOriginalQuestion] = useState("");
  const [ticketType, setTicketType] = useState("");
  const [priority, setPriority] = useState("Low");
  const [attachments, setAttachments] = useState([]);
  const [openUpload, setOpenUpload] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const resetForm = () => {
    setTicketName("");
    setOriginalQuestion("");
    setTicketType("");
    setPriority("Low");
    setAttachments([]);
    setErrors({});
    setOpenUpload(false);
  };

  // const handleAdd = () => {
  //   const payload = {
  //     ticketName,
  //     originalQuestion,
  //     ticketType,
  //     pageList: "",
  //     priority,
  //     attachments,
  //   };
  //   handleSubmitTicket(payload);
  // };

  const handleAdd = async () => {
    const payload = {
      ticketName,
      originalQuestion,
      ticketType,
      pageList: "",
      priority,
      attachments,
    };
    setSubmitting(true);
    try {
      await handleSubmitTicket(payload);
      resetForm();
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog
      fullScreen={fullScreen}
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{translate("AddNewTicket")}</Typography>
          <IconButton edge="end" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={2}>
          {/* Projet */}
          <FormControl fullWidth size="small">
            <InputLabel>{translate("Project")}</InputLabel>
            <Select
              value={selectedProject}
              label={translate("Project")}
              onChange={(e) => handleProjectChange(e.target.value)}
            >
              {accessibleProjects.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Nom */}
          <TextField
            label={translate("TicketNameForm")}
            fullWidth
            size="small"
            value={ticketName}
            error={!!errors.ticketName}
            helperText={errors.ticketName && translate("RequiredField")}
            onChange={(e) => setTicketName(e.target.value)}
          />

          {/* Description */}
          <TextField
            label={translate("WriteDescription")}
            fullWidth
            size="small"
            multiline
            rows={4}
            value={originalQuestion}
            error={!!errors.originalQuestion}
            helperText={errors.originalQuestion && translate("RequiredField")}
            onChange={(e) => setOriginalQuestion(e.target.value)}
          />

          {/* Priorité */}
          <Box display="flex" gap={1} flexWrap="wrap">
            {PRIORITY_LEVELS.map((level) => (
              <Box
                key={level}
                onClick={() => setPriority(level)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 1,
                  borderRadius: "20px",
                  border:
                    priority === level
                      ? `2px solid ${PRIORITY_COLORS[level]}`
                      : "1px solid lightgray",
                  cursor: "pointer",
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: PRIORITY_COLORS[level],
                  }}
                />
                <Typography variant="body2">{translate(level)}</Typography>
              </Box>
            ))}
          </Box>

          {/* Pièces jointes */}
          <Button variant="outlined" onClick={() => setOpenUpload(true)}>
            {translate("ManageFile")}
          </Button>
          <UploadFilesModal
            open={openUpload}
            onClose={() => setOpenUpload(false)}
            files={attachments}
            setFiles={setAttachments}
          />

          {/* Type de ticket */}
          <FormControl fullWidth size="small" error={!!errors.ticketType}>
            <InputLabel>{translate("TicketType")}</InputLabel>
            <Select
              value={ticketType}
              label={translate("TicketType")}
              onChange={(e) => setTicketType(e.target.value)}
            >
              {TICKET_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {translate(type)}
                </MenuItem>
              ))}
            </Select>
            {errors.ticketType && (
              <FormHelperText>{translate("RequiredField")}</FormHelperText>
            )}
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button onClick={handleClose}>{translate("Cancel")}</Button>
        {/* <Button variant="contained" onClick={handleAdd} disabled={loading}>
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            translate("AddNewTicket")
          )}
        </Button> */}
        <Button variant="contained" onClick={handleAdd} disabled={submitting}>
          {submitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            translate("AddNewTicket")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
